import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumberCompact, formatCurrencyUSD, formatPercent } from '../../utils/format';
import { useSettings } from '../../contexts/SettingsContext';
import ChartExportButton from './ChartExportButton';

/**
 * Pie Chart Component
 * Displays proportional data in a circular chart
 * Perfect for showing distributions and percentages
 */
const PieChart = ({ 
  data = [], 
  label = '',
  formatType = 'number',
  showPercentages = true,
  innerRadius = 0, // For donut charts (0-1 as ratio of outer radius)
  colors = null, // Optional custom color array
  showExport = true,
  exportFilename = 'chart'
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isCompact, setIsCompact] = useState(false);
  const chartContainerRef = useRef(null);
  const svgRef = useRef(null);

  // Get settings from context
  const settings = useSettings();

  // Check if inside compact container
  useEffect(() => {
    if (chartContainerRef.current) {
      const hasCompactParent = chartContainerRef.current.closest('.chart-container-compact') !== null;
      setIsCompact(hasCompactParent);
    }
  }, []);

  // Get user preference for chart animations from settings
  const animationsEnabled = settings.chartAnimations;

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    // Proper sizing for SVG
    const width = isCompact ? 300 : 400;
    const height = isCompact ? 300 : 400;
    const centerX = width / 2;
    const centerY = height / 2;
    const outerRadius = Math.min(width, height) / 2 - 50;
    const innerRadiusValue = outerRadius * innerRadius;

    // Calculate total and percentages
    const total = data.reduce((sum, item) => sum + (typeof item.value === 'number' ? item.value : 0), 0);
    
    // Default color palette
    const defaultColors = [
      '#53EF97', '#2e8b57', '#00d4aa', '#00b4d8', 
      '#5e60ce', '#7209b7', '#f72585', '#ff006e',
      '#fb8500', '#ffb703', '#ff9f1c', '#f77f00'
    ];

    const chartColors = colors || defaultColors;

    // Calculate angles for each slice
    let currentAngle = -90; // Start at top
    const slices = data.map((item, index) => {
      const value = typeof item.value === 'number' ? item.value : 0;
      const percentage = total > 0 ? (value / total) * 100 : 0;
      const angle = (value / total) * 360;
      
      const startAngle = currentAngle;
      const endAngle = currentAngle + angle;
      
      currentAngle = endAngle;

      // Calculate path for slice
      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;
      
      const x1 = centerX + outerRadius * Math.cos(startAngleRad);
      const y1 = centerY + outerRadius * Math.sin(startAngleRad);
      const x2 = centerX + outerRadius * Math.cos(endAngleRad);
      const y2 = centerY + outerRadius * Math.sin(endAngleRad);

      const largeArc = angle > 180 ? 1 : 0;

      // Build path for pie slice (full circle from center)
      const outerPath = `M ${centerX} ${centerY} L ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
      
      // Build path for donut slice (arc with inner radius)
      let path = outerPath;
      if (innerRadius > 0) {
        const x3 = centerX + innerRadiusValue * Math.cos(endAngleRad);
        const y3 = centerY + innerRadiusValue * Math.sin(endAngleRad);
        const x4 = centerX + innerRadiusValue * Math.cos(startAngleRad);
        const y4 = centerY + innerRadiusValue * Math.sin(startAngleRad);
        const innerLargeArc = angle > 180 ? 1 : 0;
        // Proper donut path: outer arc -> line to inner -> inner arc (reverse) -> line to start -> close
        path = `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadiusValue} ${innerRadiusValue} 0 ${innerLargeArc} 0 ${x4} ${y4} Z`;
      }

      // Calculate label position (midpoint of slice)
      const labelAngleRad = ((startAngle + angle / 2) * Math.PI) / 180;
      const labelRadius = (outerRadius + innerRadiusValue) / 2;
      const labelX = centerX + labelRadius * Math.cos(labelAngleRad);
      const labelY = centerY + labelRadius * Math.sin(labelAngleRad);

      return {
        ...item,
        label: item.label || item.name || `Item ${index + 1}`,
        value,
        percentage,
        angle,
        path,
        color: chartColors[index % chartColors.length],
        labelX,
        labelY,
        index
      };
    });

    return {
      slices,
      total,
      centerX,
      centerY,
      outerRadius,
      innerRadius: innerRadiusValue,
      width,
      height
    };
  }, [data, innerRadius, colors, isCompact]);

  const formatValue = (value) => {
    switch (formatType) {
      case 'currency':
        return formatCurrencyUSD(value);
      case 'percent':
        return formatPercent(value);
      case 'number':
      default:
        return formatNumberCompact(value);
    }
  };

  if (!chartData || data.length === 0) {
    return (
      <div className="chart-container chart-empty">
        <div className="chart-label">{label}</div>
        <div className="chart-placeholder">No data available</div>
      </div>
    );
  }

  return (
    <div 
      className="chart-container pie-chart" 
      ref={chartContainerRef}
      style={{ position: 'relative' }}
    >
      <div className="chart-header">
        {label && <div className="chart-label">{label}</div>}
        {showExport && (
          <ChartExportButton 
            chartRef={chartContainerRef}
            filename={exportFilename || label || 'pie-chart'}
            formats={['png', 'svg', 'pdf']}
          />
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg 
        ref={svgRef}
        className="chart-svg pie-chart-svg" 
          width={chartData.width}
          height={chartData.height}
        viewBox={`0 0 ${chartData.width} ${chartData.height}`}
          preserveAspectRatio="xMidYMid meet"
        aria-label={label || 'Pie Chart'}
      >
        {/* Slices */}
        {chartData.slices.map((slice) => {
          const isHovered = hoveredIndex === slice.index;
          return (
            <g key={`slice-${slice.index}`}>
              <path
                d={slice.path}
                fill={slice.color}
                fillOpacity={isHovered ? 1 : 0.9}
                stroke="var(--admin-bg, #000B04)"
                strokeWidth={isHovered ? '3' : '2'}
                className="pie-chart-slice"
                onMouseEnter={() => setHoveredIndex(slice.index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ 
                  cursor: 'pointer', 
                  transition: animationsEnabled ? 'all 0.2s ease' : 'none'
                }}
              />
              
              {/* Percentage label */}
              {showPercentages && slice.percentage > 5 && (
                <text
                  x={slice.labelX}
                  y={slice.labelY}
                  fill="var(--admin-text-primary, #fff)"
                  fontSize="14"
                  fontWeight="600"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pie-chart-label"
                  style={{ pointerEvents: 'none' }}
                >
                  {slice.percentage.toFixed(1)}%
                </text>
              )}
            </g>
          );
        })}

        {/* Center text for donut charts */}
        {innerRadius > 0 && (
          <g>
          <text
            x={chartData.centerX}
              y={chartData.centerY - 8}
              fill="var(--admin-text-primary, #fff)"
              fontSize="16"
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="middle"
            className="pie-chart-center-label"
          >
            Total
          </text>
          <text
            x={chartData.centerX}
              y={chartData.centerY + 12}
              fill="var(--admin-text-secondary, rgba(255,255,255,0.7))"
              fontSize="20"
              fontWeight="700"
            textAnchor="middle"
            dominantBaseline="middle"
            className="pie-chart-center-value"
          >
            {formatValue(chartData.total)}
          </text>
          </g>
        )}
      </svg>
      </div>

      {/* Legend */}
      <div className="pie-chart-legend">
        {chartData.slices.map((slice) => {
          const isHovered = hoveredIndex === slice.index;
          return (
            <div 
              key={`legend-${slice.index}`}
              className={`pie-chart-legend-item ${isHovered ? 'hovered' : ''}`}
              onMouseEnter={() => setHoveredIndex(slice.index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <span 
                className="pie-chart-legend-color"
                style={{ backgroundColor: slice.color }}
              ></span>
              <span className="pie-chart-legend-label">{slice.label}</span>
              <span className="pie-chart-legend-value">
                {formatValue(slice.value)} ({slice.percentage.toFixed(1)}%)
              </span>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {settings.showTooltips && hoveredIndex !== null && chartData.slices[hoveredIndex] && (
        <div 
          className="chart-tooltip pie-chart-tooltip"
          style={{
            position: 'absolute',
            pointerEvents: 'none'
          }}
        >
          <div className="chart-tooltip-header">
            <div className="chart-tooltip-label">
              {chartData.slices[hoveredIndex].label}
            </div>
          </div>
          <div className="chart-tooltip-content">
            <div className="chart-tooltip-item">
              <div className="chart-tooltip-value">
                {formatValue(chartData.slices[hoveredIndex].value)}
              </div>
              <div className="chart-tooltip-subtitle">
                {chartData.slices[hoveredIndex].percentage.toFixed(2)}% of total
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  label: PropTypes.string,
  formatType: PropTypes.oneOf(['number', 'currency', 'percent']),
  showPercentages: PropTypes.bool,
  innerRadius: PropTypes.number, // 0-1 ratio for donut charts
  colors: PropTypes.arrayOf(PropTypes.string),
  showExport: PropTypes.bool,
  exportFilename: PropTypes.string
};

export default PieChart;
